import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TablePagination,
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Collapse,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import { ArrowBackIosNewRounded } from "@mui/icons-material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./planSelection.module.css";
import { useLocalStorage } from "@uidotdev/usehooks";
import { ReactComponent as Hemberger } from "../assets/icons/Hemberger.svg";
import { ReactComponent as UserIcon } from "../assets/icons/UserIcon.svg";
import Sidebar from "../sidenav/Sidenav";
import { Link, useNavigate } from "react-router-dom";
import {
  findBackendTabByName,
  getStoredBackendTabs,
  getStoredTabIds,
  isAdminUser,
} from "../utils/access";
import roleService from "../roles/roleService";

const PlanSelectionMobile = () => {
  // data
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // ui
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // auth
  const [token] = useLocalStorage("token", null);
  const userTabIds = getStoredTabIds();
  const [backendTabs, setBackendTabs] = useState(getStoredBackendTabs);
  const [tabsLoading, setTabsLoading] = useState(() => getStoredBackendTabs().length === 0);
  const planSelectedTab = findBackendTabByName(
    backendTabs,
    "Plan Selected"
  );
  const isAuthorized =
    isAdminUser() ||
    tabsLoading ||
    (planSelectedTab ? userTabIds.includes(planSelectedTab.tab_id) : false);

  const navigate = useNavigate();

  const closeSidebar = () => setIsSidebarOpen(false);

  function formatDate(dt) {
    const d = new Date(dt);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return {
      date: `${day}.${month}.${year}`,
      time: `${hours}:${minutes}${seconds ? ":" + seconds : ""}`,
    };
  }

  const fetchAll = async () => {
    try {
      const baseUrl = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}`;
      const url = `${baseUrl}/plan-selections?limit=200&offset=0&sort=selected_at.desc`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = res?.data?.data || [];
      const normalized = rows.map((r) => ({
        select_id: r.id ?? r.select_id ?? "-",
        user_id: r.user_id ?? r.uid ?? r.userId ?? "-",
        class: r.class ?? "-",
        goal: r.goal ?? "-",
        plan_name: r.plan_name ?? "-",
        selected_at: r.selected_at,
      }));
      setItems(normalized);
      setFiltered(normalized);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Plan Selections.");
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let isMounted = true;

    const fetchTabs = async () => {
      if (!token || isAdminUser()) return;

      setTabsLoading(true);
      try {
        const activeTabs = await roleService.getTabs(token);
        if (!isMounted) return;

        setBackendTabs(activeTabs);
        localStorage.setItem("backend_tabs", JSON.stringify(activeTabs));
      } catch (error) {
        console.error("Error fetching tabs:", error);
      } finally {
        if (isMounted) setTabsLoading(false);
      }
    };

    fetchTabs();

    return () => {
      isMounted = false;
    };
  }, [token]);

  // search: by user_id or plan_name (since name/email aren’t available)
  useEffect(() => {
    if (!searchQuery) {
      setFiltered(items);
      setPage(0);
      return;
    }
    const q = searchQuery.toLowerCase();
    const f = items.filter((x) => {
      const uid = String(x.user_id ?? "").toLowerCase();
      const plan = (x.plan_name ?? "").toLowerCase();
      return uid.includes(q) || plan.includes(q);
    });
    setFiltered(f);
    setPage(0);
  }, [searchQuery, items]);

  // pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayed = filtered.slice(startIndex, endIndex);
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (!isAuthorized) {
    return (
      <div className="container-fluid mt-5">
        <h2 style={{ marginTop: "5%" }}>You are unauthorized to access this page</h2>
      </div>
    );
  }

  // Row (collapsible) – mirrors SubscriptionsMobile behavior
  function Row({ row }) {
    const [open, setOpen] = useState(false);
    const { date, time } = formatDate(row.selected_at);

    return (
      <React.Fragment>
        <TableRow>
          <TableCell style={{ maxWidth: "30ch", wordWrap: "break-word" }}>
            <div><b>User ID: {row.user_id}</b></div>
            <div>Class: {row.class}</div>
            <div>Goal: {row.goal}</div>
            <div>Plan: {row.plan_name}</div>
          </TableCell>
          <TableCell align="right">
            <div>{date}</div>
            <div>{time}</div>
          </TableCell>
          <TableCell>
            <IconButton
              aria-label="expand row"
              style={{ color: "black" }}
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
          </TableCell>
        </TableRow>

        <TableRow
          sx={{
            "& td": { border: 0, height: open ? "auto" : 0 },
            borderBottom: "1px solid black",
          }}
        >
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  rowGap: "6px",
                  fontSize: "10px",
                  marginTop: "4px",
                  marginBottom: "4px",
                }}
              >
                <div>S. No.: {row.select_id}</div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <>
      {/* Top bar: hamburger + profile (match SubscriptionsMobile) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0.5rem 0.75rem 1.25rem 0.75rem",
        }}
      >
        <div
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            width: "36px",
            height: "36px",
            borderRadius: "18px",
            background: "#EAF1F7",
          }}
        >
          <Hemberger />
        </div>
        <Link to="/profile">
          <UserIcon />
        </Link>
      </div>
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />

      <Card style={{ boxShadow: "none", padding: 0 }}>
        <CardContent style={{ padding: 0 }}>
          {/* Header row: back + title + search */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "0rem 0.75rem 1.5rem 0.75rem",
            }}
          >
            <div style={{ display: "flex", gap: "1vw", alignItems: "center" }}>
              <ArrowBackIosNewRounded
                onClick={() => navigate("/dashboard")}
                style={{ cursor: "pointer" }}
              />
              <h4 style={{ marginRight: "1rem", marginBottom: 0 }}>Plan Selected</h4>
            </div>

            <TextField
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent",
                    boxShadow: "none",
                  },
                },
              }}
              InputProps={{
                style: {
                  height: "1.75rem",
                  width: "9.75rem",
                  boxShadow:
                    "0px 1.74px 3.48px 0px #0000001A inset, 0px -1.74px 3.8px 0px #C8C7C740 inset, 0px 1.74px 3.48px 0px #0000001A",
                  padding: 0,
                },
                endAdornment: (
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </div>

          {/* Pagination (same placement as SubscriptionsMobile) */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-input": {
                marginRight: "0.5rem",
                marginLeft: 0,
              },
            }}
          />

          <ToastContainer />

          {/* Table (collapsible rows) */}
          <Table
            aria-label="plan-selections"
            sx={{
              [`& .${tableCellClasses.root}`]: { borderBottom: "none" },
            }}
          >
            <TableHead
              style={{
                height: "2.564vw",
                borderRadius: "4px !important",
                background: "rgba(234, 241, 247, 1)",
                boxShadow: "2px 2px 4px 0px rgba(174, 174, 192, 0.5)",
              }}
            >
              <TableRow>
                <TableCell>User • Class • Goal • Plan</TableCell>
                <TableCell align="right">Selected (Date & Time)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((row, idx) => (
                <Row key={`${row.select_id}-${row.user_id}-${idx}`} row={row} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
};

export default PlanSelectionMobile;
